import { useState, useEffect, useContext } from "react";
import { SubcategoryDetailProps } from "../constants/settingsStructure";
import { Label } from "@/modern/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/modern/components/ui/select";
import { Card } from "@/modern/components/ui/card";
import BryntumContext from "@/react/context/bryntumContext";
// @ts-expect-error - userPreferences is dynamically set in bryntumContext
import { userPreferences } from "@/react/context/bryntumContext.jsx";
import { PERSISTENCE_STATE_KEYS } from "@/other/constants/PersistenceStateKeys.jsx";
import { toast } from "sonner";

interface RoleOption {
  name: string;
  grantId: number;
}

const PermissionDetail = ({ subcategory }: SubcategoryDetailProps) => {
  const { user, project, boardManager } = useContext(BryntumContext);

  const [roles, setRoles] = useState<RoleOption[]>([]);
  const [canEdit, setCanEdit] = useState<number | null>(null);
  const [canView, setCanView] = useState<number | null>(null);

  // Helper function to get minimum grant from role
  const getGrantForRole = (roleRecord: any): number => {
    return Math.min(...roleRecord.grants.map((g: any) => g.value));
  };

  // Load initial values
  useEffect(() => {
    if (boardManager && project && user) {
      // Get roles with grants
      // @ts-expect-error - boardManager is dynamically typed
      const appRoles = boardManager.boardNameStore?.appRoles?.filter((role: any) => role.grants.length) || [];
      const roleOptions: RoleOption[] = appRoles.map((roleRecord: any) => ({
        name: roleRecord.name,
        grantId: getGrantForRole(roleRecord)
      }));
      setRoles(roleOptions);

      // Get current board user's grant
      // @ts-expect-error - project is dynamically typed
      const boardUserRecord = project.boardUsersStore?.records?.find((u: any) => u.userId === user.id);
      if (boardUserRecord) {
        const userGrant = getGrantForRole(boardUserRecord);

        // Load saved preferences or default to user's grant
        // @ts-expect-error - userPreferences is dynamically typed
        const savedEditPref = userPreferences.getSetting(PERSISTENCE_STATE_KEYS.userDefaultEditPermission);
        // @ts-expect-error - userPreferences is dynamically typed
        const savedViewPref = userPreferences.getSetting(PERSISTENCE_STATE_KEYS.userDefaultViewPermission);

        const editValue = savedEditPref || userGrant;
        const viewValue = savedViewPref || userGrant;

        setCanEdit(editValue);
        setCanView(viewValue);

        // Set defaults if not already set
        if (!savedEditPref) {
          // @ts-expect-error - userPreferences is dynamically typed
          userPreferences.updateSetting(PERSISTENCE_STATE_KEYS.userDefaultEditPermission, userGrant);
        }
        if (!savedViewPref) {
          // @ts-expect-error - userPreferences is dynamically typed
          userPreferences.updateSetting(PERSISTENCE_STATE_KEYS.userDefaultViewPermission, userGrant);
        }
      }
    }
  }, [boardManager, project, user]);

  // Check if user can select this role
  const canSelectRole = (grantId: number): boolean => {
    if (!user) return false;
    return user.can(grantId);
  };

  // Handler for Can Edit change
  const handleCanEditChange = (value: string) => {
    const newEditGrant = parseInt(value);
    setCanEdit(newEditGrant);
    // @ts-expect-error - userPreferences is dynamically typed
    userPreferences.updateSetting(PERSISTENCE_STATE_KEYS.userDefaultEditPermission, newEditGrant);

    // If new edit grant is higher (more restrictive) than view grant, update view grant
    if (canView !== null && newEditGrant > canView) {
      setCanView(newEditGrant);
      // @ts-expect-error - userPreferences is dynamically typed
      userPreferences.updateSetting(PERSISTENCE_STATE_KEYS.userDefaultViewPermission, newEditGrant);
    }
  };

  // Handler for Can View change
  const handleCanViewChange = (value: string) => {
    const newViewGrant = parseInt(value);

    // Validate: view permission cannot be higher (lower number) than edit permission
    if (canEdit !== null && newViewGrant < canEdit) {
      toast.error("View permission cannot be higher than Edit permission");
      return;
    }

    setCanView(newViewGrant);
    // @ts-expect-error - userPreferences is dynamically typed
    userPreferences.updateSetting(PERSISTENCE_STATE_KEYS.userDefaultViewPermission, newViewGrant);
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-foreground mb-2">{subcategory.label}</h2>
        <p className="text-sm text-muted-foreground">
          {subcategory.description}
        </p>
      </div>

      <div className="space-y-5 max-w-3xl">
        {/* Default Permissions Card */}
        <Card className="py-3 px-4">
          <div className="space-y-6">
            <div className="flex items-center gap-2">
              <div>
                <h3 className="text-base font-semibold text-foreground mb-1">Default Permissions</h3>
                <p className="text-xs text-muted-foreground">Set default access levels for new entities you create</p>
              </div>
            </div>

            <div className="space-y-4">
              {/* Can Edit */}
              <div className="space-y-2">
                <Label htmlFor="can-edit">Can Edit</Label>
                <Select value={canEdit?.toString() || ""} onValueChange={handleCanEditChange} disabled={!roles.length}>
                  <SelectTrigger id="can-edit">
                    <SelectValue placeholder="Select edit permission" />
                  </SelectTrigger>
                  <SelectContent>
                    {roles.map((role) => (
                      <SelectItem
                        key={role.grantId}
                        value={role.grantId.toString()}
                        disabled={!canSelectRole(role.grantId)}
                        className={!canSelectRole(role.grantId) ? "opacity-50 cursor-not-allowed" : ""}
                      >
                        {role.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">Default permission level for editing entities you create</p>
              </div>

              {/* Can View */}
              <div className="space-y-2">
                <Label htmlFor="can-view">Can View</Label>
                <Select value={canView?.toString() || ""} onValueChange={handleCanViewChange} disabled={!roles.length}>
                  <SelectTrigger id="can-view">
                    <SelectValue placeholder="Select view permission" />
                  </SelectTrigger>
                  <SelectContent>
                    {roles.map((role) => (
                      <SelectItem
                        key={role.grantId}
                        value={role.grantId.toString()}
                        disabled={!canSelectRole(role.grantId)}
                        className={!canSelectRole(role.grantId) ? "opacity-50 cursor-not-allowed" : ""}
                      >
                        {role.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">Default permission level for viewing entities you create</p>
              </div>
            </div>
          </div>
        </Card>

        {/* Information Note Card */}
        <Card className="py-3 px-4 border-primary/20 bg-primary/5">
          <div className="flex gap-3">
            <div className="flex-shrink-0 mt-0.5">
              <div className="h-5 w-5 rounded-full bg-primary/15 flex items-center justify-center">
                <span className="text-primary text-xs font-bold">i</span>
              </div>
            </div>
            <div className="flex-1">
              <p className="text-sm text-foreground leading-relaxed">
                <strong>Important:</strong> These settings define the default permissions for new entities you create. View permission must be equal to or lower than Edit permission. Roles you don't
                have access to are disabled.
              </p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default PermissionDetail;
